import React, { useRef, useEffect } from 'react';
import { View, Text, StyleSheet, Animated, Easing, Dimensions } from 'react-native';
import LogoSvg from '../LogoSvg';

// Create animated version of LogoSvg
const AnimatedLogoSvg = Animated.createAnimatedComponent(LogoSvg);

const LoadingScreen = ({ 
    onComplete, 
    loadingText = "BET OPTIMIZED",
    cacheStatus = null // { stage: 'checking_cache' | 'cache_hit' | 'cache_miss' | 'live_api_complete' | 'error', message: string }
}) => {
    const backgroundOpacity = useRef(new Animated.Value(0)).current;
    const textOpacity = useRef(new Animated.Value(0)).current;
    const statusOpacity = useRef(new Animated.Value(0)).current;
    
    const screenWidth = Dimensions.get('window').width;
    const screenHeight = Dimensions.get('window').height;

    useEffect(() => {
        startLoadingAnimation();
    }, []);

    // Animate status message when cache status changes
    useEffect(() => {
        if (cacheStatus) {
            Animated.sequence([
                Animated.timing(statusOpacity, {
                    toValue: 0,
                    duration: 150,
                    useNativeDriver: true,
                }),
                Animated.timing(statusOpacity, {
                    toValue: 1,
                    duration: 300,
                    useNativeDriver: true,
                })
            ]).start();
        }
    }, [cacheStatus?.message]);

    const startLoadingAnimation = () => {
        // Create alternating black/white animation
        const createAlternatingAnimation = () => {
            return Animated.loop(
                Animated.sequence([
                    Animated.timing(backgroundOpacity, {
                        toValue: 1,
                        duration: 1000,
                        easing: Easing.inOut(Easing.cubic),
                        useNativeDriver: false,
                    }),
                    Animated.timing(backgroundOpacity, {
                        toValue: 0,
                        duration: 1000,
                        easing: Easing.inOut(Easing.cubic),
                        useNativeDriver: false,
                    }),
                ])
            );
        };

        // Start alternating animation
        createAlternatingAnimation().start();

        // Show text immediately and keep it visible
        Animated.timing(textOpacity, {
            toValue: 1,
            duration: 500,
            easing: Easing.out(Easing.cubic),
            useNativeDriver: false,
        }).start();

        // Complete loading after 3 seconds
        setTimeout(() => {
            if (onComplete) onComplete();
        }, 3000);
    };

    return (
        <View style={[styles.container, { width: screenWidth, height: screenHeight }]}>
            <Animated.View 
                style={[
                    styles.backgroundOverlay,
                    {
                        backgroundColor: backgroundOpacity.interpolate({
                            inputRange: [0, 1],
                            outputRange: ['rgba(0, 0, 0, 1)', 'rgba(255, 255, 255, 1)'],
                        }),
                    }
                ]}
            />
            
            <View style={styles.content}>
                <Animated.View style={[styles.textContainer, { opacity: textOpacity }]}>
                    <View style={styles.titleContainer}>
                        <View style={styles.logoContainer}>
                            <AnimatedLogoSvg 
                                width={32} 
                                height={32} 
                                color={backgroundOpacity.interpolate({
                                    inputRange: [0, 1],
                                    outputRange: ['rgba(255, 255, 255, 0.80)', 'rgba(0, 0, 0, 0.80)'],
                                })}
                                spin={true}
                            />
                        </View>
                        <Animated.Text style={[
                            styles.title,
                            {
                                color: backgroundOpacity.interpolate({
                                    inputRange: [0, 1],
                                    outputRange: ['rgba(255, 255, 255, 0.80)', 'rgba(0, 0, 0, 0.80)'],
                                }),
                            }
                        ]}>
                            {loadingText}
                        </Animated.Text>
                        <View style={styles.logoContainer}>
                            <AnimatedLogoSvg 
                                width={32} 
                                height={32} 
                                color={backgroundOpacity.interpolate({
                                    inputRange: [0, 1],
                                    outputRange: ['rgba(255, 255, 255, 0.80)', 'rgba(0, 0, 0, 0.80)'],
                                })}
                                spin={true}
                            />
                        </View>
                    </View>
                    
                    {/* Cache Status Message */}
                    {cacheStatus && cacheStatus.message && (
                        <Animated.View style={[styles.statusContainer, { opacity: statusOpacity }]}>
                            <Animated.Text style={[
                                styles.statusText,
                                cacheStatus.stage === 'cache_miss' && styles.statusTextHighlight,
                                {
                                    color: backgroundOpacity.interpolate({
                                        inputRange: [0, 1],
                                        outputRange: ['rgba(255, 255, 255, 0.65)', 'rgba(0, 0, 0, 0.65)'],
                                    }),
                                }
                            ]}>
                                {cacheStatus.stage === 'checking_cache' && '🔍 '}
                                {cacheStatus.stage === 'cache_hit' && '⚡ '}
                                {cacheStatus.stage === 'cache_miss' && '🔎 '}
                                {cacheStatus.stage === 'error' && '⚠️ '}
                                {cacheStatus.message}
                            </Animated.Text>
                        </Animated.View>
                    )}
                </Animated.View>
            </View>
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        paddingLeft: 48,
        paddingRight: 48,
        backgroundColor: 'white',
        overflow: 'hidden',
        borderWidth: 2,
        borderColor: 'black',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
    },
    backgroundOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
    },
    content: {
        alignSelf: 'stretch',
        paddingTop: 16,
        paddingBottom: 16,
        backgroundColor: 'rgba(255, 255, 255, 0)',
        justifyContent: 'center',
        alignItems: 'center',
        gap: 303,
    },
    textContainer: {
        height: 32,
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 10,
        flexDirection: 'row',
    },
    titleContainer: {
        height: 32,
        justifyContent: 'flex-start',
        alignItems: 'center',
        gap: 10,
        flexDirection: 'row',
    },
    title: {
        fontSize: 40,
        fontFamily: 'ClimateCrisis',
        fontWeight: '400',
        textTransform: 'uppercase',
        lineHeight: 40,
    },
    logoContainer: {
        width: 32,
        height: 32,
        position: 'relative',
        overflow: 'visible',
        justifyContent: 'center',
        alignItems: 'center',
    },
    statusContainer: {
        marginTop: 24,
        paddingHorizontal: 20,
        paddingVertical: 12,
        borderRadius: 8,
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        maxWidth: '80%',
    },
    statusText: {
        fontSize: 14,
        fontWeight: '500',
        textAlign: 'center',
        lineHeight: 20,
    },
    statusTextHighlight: {
        fontWeight: '700',
        fontSize: 15,
    },
});

export default LoadingScreen;
